import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'antd';

import { Giide, IState } from '../graphql/types';
import useUser from './useUser';
import GiideSettings from '../components/GiideSettings/GiideSettings';
import { PublishState } from '../graphql/api';

interface IModalProps {
  className?: string;
}

export enum ModalContent {
  settings,
  publish,
  preview,
  share,
  none,
}

const useGiideContentModal = () => {
  const giide = useSelector<IState, Giide | undefined>(state => state.giide);
  const [modalContent, setModalContent] = useState<ModalContent | null>(null);
  useState<boolean>(false);
  const { user } = useUser();

  const closeModal = useCallback(() => {
    setModalContent(ModalContent.none);
  }, []);

  const missingRecording = useSelector<IState, boolean>(
    state =>
      state.steps.filter(
        s =>
          !s.recording || s.recording.deleted || !s.recording.transcriptResults
      ).length > 0
  );

  const missingChapters = useSelector<IState, boolean>(
    state => state.steps.length === 0
  );

  const tooltipMessagePreviewAndPublish = useMemo<string>(
    () =>
      giide?.publishState === PublishState.migrating
        ? 'Preparing your giide preview'
        : giide?.publishState === PublishState.transcoding
        ? 'The giide data is being transcoded'
        : missingRecording
        ? 'One of your steps has no audio'
        : missingChapters
        ? 'Create at least one chapter to publish your giide'
        : '',
    [giide, missingRecording, missingChapters]
  );

  const tooltipMessageShareLink = useMemo<string>(
    () =>
      giide?.publishState === PublishState.migrating
        ? 'Preparing your giide preview'
        : giide?.publishState !== PublishState.published
        ? 'Your giide is not published'
        : !giide?.unauthenticatedAccessAllowed
        ? 'Your giide does not allow unauthenticated access'
        : '',
    [giide]
  );

  const tooltipMessageUnpublish = useMemo<string>(
    () =>
      giide?.publishState === PublishState.migrating
        ? 'Preparing your giide preview'
        : giide?.publishState !== PublishState.published
        ? 'Your giide is not published'
        : !(user?.userRole === 'admin' || user?.userId === giide.owner)
        ? 'You do not have permissions to do it'
        : '',
    [giide, user]
  );

  const tooltipMessageSettingsAndDelete = useMemo<string>(
    () =>
      giide?.publishState === PublishState.migrating
        ? 'Preparing your giide preview'
        : '',
    [giide]
  );

  const closeSettingsModal = useCallback(() => {
    setModalContent(null);
    closeModal();
  }, [setModalContent, closeModal]);

  const GiideContentModal: React.FC<IModalProps> = ({ className }) => {
    if (!(giide?.id && user?.tenantID && user.userId)) {
      return null;
    }

    return (
      <>
        <Modal
          open={modalContent === ModalContent.settings}
          onCancel={closeModal}
        >
          <GiideSettings
            giide={giide}
            tenantId={user.tenantID}
            userId={user.userId}
            closeModal={closeSettingsModal}
          />
        </Modal>
      </>
    );
  };

  return {
    GiideContentModal,
    setModalContent,
    ModalContent,
    tooltipMessagePreviewAndPublish,
    tooltipMessageShareLink,
    tooltipMessageUnpublish,
    tooltipMessageSettingsAndDelete,
  };
};

export default useGiideContentModal;
